/**
 * Layer 3 — team AI, 5Hz. Phase FSM, formation solver, space analysis. (§10.1)
 *
 * This file also owns the orchestration: it is the thing the server holds, it
 * runs all three layers at their three rates, and it is the only place an
 * InputCommand is handed back.
 *
 * ── THE RULE THIS WHOLE MODULE EXISTS TO OBEY (§10.1) ───────────────────────
 *
 * The AI's entire output is an array of InputCommands. It never writes to
 * WorldState. Not "tries not to" — there is no code path here that could: the
 * world arrives as a parameter, is read, and every conclusion is expressed as
 * moveX/moveZ/aimYaw/flags, which is exactly what a keyboard produces. The
 * player controller (§3.3), the ball model (§3.2) and the rules (§5) cannot
 * distinguish this from a human, which is what makes "the AI is never
 * superhuman" a structural guarantee rather than a promise. It is also what
 * makes a human dropping into an AI slot mid-match a no-op.
 *
 * ── Three rates, one loop ───────────────────────────────────────────────────
 *
 *   Layer 3 (this file, 5Hz)      phase, shape, assignments, influence map
 *   Layer 2 (individual.ts, 10Hz) the behaviour tree → a decision
 *   Layer 1 (steering.ts, 60Hz)   context steering → moveX/moveZ
 *
 * The rates are not a performance trick — they are the model. A team that
 * re-shapes 60 times a second has no shape; a player who re-decides every tick
 * has no commitment. The staleness IS the behaviour.
 *
 * ── Randomness: why this does NOT draw from world.rng ───────────────────────
 *
 * The obvious call is `pcg32Float(state.rng)`, and it is wrong here. The AI runs
 * BEFORE step(), outside the sim. Drawing from world.rng would advance the sim's
 * stream from outside the sim, and then replaying a recorded input tape through
 * step() — which is what the determinism test (§13.1), the replay viewer and
 * every desync investigation do — would produce a different stream than the live
 * match did, because the AI's draws are not in the tape. Every tackle roll after
 * the first AI decision would diverge. `step()` must stay a pure function of
 * (state, inputs), and that means the AI's dice live outside it.
 *
 * So the AI keys its own generator on (matchSeed, tick, playerId, salt) and
 * derives it fresh each time — stateless, so it is identical for any instance at
 * any time, survives a server rewind, and cannot drift from a re-created AI.
 * Deterministic in the way that actually matters, with no hidden state to clone.
 * See the report.
 */

import type { InputCommand, WorldState } from '../sim/types.js';
import { InputFlags, MAX_PLAYERS, MatchPhase, TacticalCommand, emptyInput } from '../sim/types.js';
import {
  AI_INTERCEPT_ITERATIONS,
  AI_BLIND_DISTANCE_PENALTY,
  AI_BLOCK_BALL_SHIFT_SCALE,
  AI_CALL_FOR_PASS_DURATION_TICKS,
  AI_CIRCULATE_DURATION_TICKS,
  AI_BLOCK_BALL_SHIFT_X,
  AI_BLOCK_BALL_SHIFT_Z,
  AI_SHAPE_BALL_LEAD_MAX_SPEED,
  AI_SHAPE_BALL_LEAD_S,
  AI_BUILD_UP_FX,
  AI_DEFENDER_PHASE_GAIN,
  AI_FINAL_THIRD_FX,
  AI_FORWARD_DROP_DAMP,
  AI_FORWARD_PHASE_GAIN,
  AI_FULLBACK_ATTACK_PUSH,
  AI_INDISCIPLINE_BALL_CHASE,
  AI_INDISCIPLINE_CHASE_SCALE,
  AI_INDIVIDUAL_TICK_RATE,
  AI_MARK_SWAP_MARGIN,
  AI_MAX_CONCURRENT_PRESSERS,
  AI_OFFER_BAND_HYSTERESIS,
  AI_OFFER_MAX_DISTANCE,
  AI_OFFER_MIN_DISTANCE,
  AI_PASS_INTENT_MARGIN_TICKS,
  AI_PASS_INTENT_TICKS,
  AI_PHASE_SHIFT_ATTACK,
  AI_PHASE_SHIFT_BUILD_UP,
  AI_PHASE_SHIFT_DEFEND_BLOCK,
  AI_PHASE_SHIFT_FINAL_THIRD,
  AI_PHASE_SHIFT_PRESS,
  AI_PHASE_SHIFT_TRANSITION,
  AI_POSSESSION_PUSH,
  AI_POSSESSION_PUSH_DEFENDER_DAMP,
  AI_PRESS_LINE_PUSH,
  AI_RECEIVE_HYSTERESIS_TICKS,
  AI_SECOND_PRESS_RADIUS,
  AI_SECOND_PRESS_RADIUS_PER_COUNT,
  AI_SEEK_ARRIVE_RADIUS,
  MAX_SPEED_RUN,
  AI_SEEK_ARRIVE_RADIUS_URGENT,
  AI_SHAPE_SPREAD_FZ,
  AI_STEER_SHAPE_WEIGHT,
  AI_TEAM_TICK_RATE,
  AI_TRANSITION_TICKS,
  SIMULATION_TICK_RATE,
} from '../sim/constants.js';
import {
  POSITION_ANCHORS,
  isDefenderPosition,
  isForwardPosition,
  isFullbackPosition,
} from './formations.js';
import type { Difficulty, DifficultyParams } from './difficulty.js';
import { DIFFICULTY_PARAMS, reactionMsToTicks } from './difficulty.js';
import type { AiBrain } from './individual.js';
import {
  Assignment,
  SALT_REACTION,
  TeamPhase,
  applyDecisionToInput,
  canSee,
  leaveDuelIfPossessionChanged,
  runIndividualAi,
  sampleEngageEpisode,
} from './individual.js';
import { InfluenceMap } from './influence.js';
import { createIntercept, solveIntercept } from './ball-travel.js';
// ── SITUATIONAL SHAPE seam (ai/situational-shape.ts). Deliberately ONE import
// and one contiguous block inside solveFormation, so this feature rebases
// cleanly past whatever else is in flight in this file. ─────────────────────
import type { ShapeAdjust, ShapeSituation } from './situational-shape.js';
import {
  createShapeAdjust,
  createShapeSituation,
  describeShapeSituation,
  shapeBandOf,
  situationalShape,
} from './situational-shape.js';
import type { MoveOut, SteerRequest } from './steering.js';
import { createMoveOut, createSteerRequest, steer } from './steering.js';
import type { Pcg32 } from '../math/pcg32.js';
import { pcg32Create, pcg32Float, pcg32Seed } from '../math/pcg32.js';
import { clamp, lerp } from '../math/vec3.js';

// ---------------------------------------------------------------------------
// Public contract — what the server codes against
// ---------------------------------------------------------------------------

export interface TeamAI {
  /**
   * Fill `out` with this team's AI inputs for the CURRENT tick of `world`.
   *
   * Contract:
   *  - Only slots belonging to this team are touched. Never the opponent's.
   *  - A slot that already holds a non-null input is left ALONE: non-null means
   *    somebody else owns it (a human). The server's natural loop — write the
   *    humans, then call this — therefore Just Works, and so does overwriting
   *    ours afterwards.
   *  - Goalkeepers are never filled: sim/goalkeeper.ts is a complete keeper AI
   *    and a non-null input on a GK slot switches it into human-control mode.
   *  - `world` is READ ONLY. This method does not mutate it. (§10.1)
   */
  produceInputs(world: WorldState, out: Array<InputCommand | null>): void;
  /** §6 Test Arena — this slot's current Layer-3 assignment as a short label
   *  (Hold/Support/Carry/Chase/Press/Mark/Cover), for the arena's AI-role readout.
   *  Read-only; a slot this brain does not own returns 'Hold'. */
  roleName(id: number): string;
  /** §3 — the teammate committed to collecting the current loose ball / receiving
   *  the current pass, or -1. Read-only diagnostic. */
  receiverId(): number;
}

/** Assignment enum (individual.ts) order → label, for `roleName`. */
const ASSIGNMENT_LABELS = ['Hold', 'Support', 'Carry', 'Chase', 'Press', 'Mark', 'Cover', 'Offer'] as const;

/** Phase shift per TeamPhase, indexed by the enum. Built once. */
const PHASE_SHIFT = new Float64Array(6);
PHASE_SHIFT[TeamPhase.BuildUp] = AI_PHASE_SHIFT_BUILD_UP;
PHASE_SHIFT[TeamPhase.Attack] = AI_PHASE_SHIFT_ATTACK;
PHASE_SHIFT[TeamPhase.FinalThird] = AI_PHASE_SHIFT_FINAL_THIRD;
PHASE_SHIFT[TeamPhase.Transition] = AI_PHASE_SHIFT_TRANSITION;
PHASE_SHIFT[TeamPhase.Press] = AI_PHASE_SHIFT_PRESS;
PHASE_SHIFT[TeamPhase.DefendBlock] = AI_PHASE_SHIFT_DEFEND_BLOCK;

/** Scratch for the loose-ball intercept solve. Layer 3 is not re-entrant. (§15.7) */
const interceptScratch = createIntercept();

const TEAM_PERIOD = Math.round(SIMULATION_TICK_RATE / AI_TEAM_TICK_RATE); // 12
const INDIVIDUAL_PERIOD = Math.round(SIMULATION_TICK_RATE / AI_INDIVIDUAL_TICK_RATE); // 6

/** murmur3 fmix32. Integer ops only ⇒ bit-identical on every engine. (D2) */
function mix32(x: number): number {
  let h = x | 0;
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

function distXZ(ax: number, az: number, bx: number, bz: number): number {
  const dx = ax - bx;
  const dz = az - bz;
  return Math.sqrt(dx * dx + dz * dz);
}

// ---------------------------------------------------------------------------
// The implementation
// ---------------------------------------------------------------------------

class TeamAiImpl implements TeamAI, AiBrain {
  world: WorldState;
  readonly team: number;
  readonly params: DifficultyParams;
  readonly influence = new InfluenceMap();

  attackSign = 1;
  phase: TeamPhase = TeamPhase.BuildUp;
  onsideLimitProj = Number.POSITIVE_INFINITY;

  // Layer 3 output.
  readonly assignment = new Uint8Array(MAX_PLAYERS);
  readonly markTarget = new Int32Array(MAX_PLAYERS);
  /** Last cycle's marking, for hysteresis — a defender keeps his man unless a
   *  different opponent is CLEARLY closer, so markers don't swap every 5Hz
   *  cycle ("players roam like idiots"). -1 = wasn't marking. */
  private readonly prevMarkTarget = new Int32Array(MAX_PLAYERS).fill(-1);
  /** Was this man OFFERING last Layer-3 cycle? The band gate below is sticky,
   *  see AI_OFFER_BAND_HYSTERESIS. 1 = yes. */
  private readonly wasOffering = new Uint8Array(MAX_PLAYERS);
  /** §3 — the teammate committed to collecting the current loose ball / receiving
   *  the current pass, for hysteresis (kept until clearly beaten). -1 = none. */
  private looseChaser = -1;
  readonly humanMask = new Uint8Array(MAX_PLAYERS);
  /** 1 ⇒ this slot is actively calling for the ball this decision (§8). */
  readonly callActive = new Uint8Array(MAX_PLAYERS);
  /** Sim tick until which each slot's call stays active (−1 = not calling). */
  private readonly callExpiryTick = new Int32Array(MAX_PLAYERS).fill(-1);
  /** §8 — the human's team CIRCULATE command, held to this tick (−1 = none). */
  private circulateExpiryTick = -1;
  /** §8 — true while a circulate is live (read by decideOnBall via AiBrain). */
  circulateActive = false;
  readonly shapeX = new Float64Array(MAX_PLAYERS);
  readonly shapeZ = new Float64Array(MAX_PLAYERS);

  // Layer 2 output.
  readonly decSeekX = new Float64Array(MAX_PLAYERS);
  readonly decSeekZ = new Float64Array(MAX_PLAYERS);
  readonly decAimYaw = new Float64Array(MAX_PLAYERS);
  readonly decFlags = new Int32Array(MAX_PLAYERS);
  readonly decKick = new Uint8Array(MAX_PLAYERS);
  /** Tick the latched decKick was decided on — the strike buffer's age stamp
   *  (individual.ts applyDecisionToInput). */
  readonly decKickTick = new Int32Array(MAX_PLAYERS);
  readonly decAction = new Uint8Array(MAX_PLAYERS);
  readonly decCharge = new Float64Array(MAX_PLAYERS);
  readonly decLift = new Float64Array(MAX_PLAYERS);
  /** §3 — who each slot's pending pass is FOR (individual.ts emitPass). */
  readonly decPassTarget = new Int32Array(MAX_PLAYERS).fill(-1);
  readonly kickCooldown = new Int32Array(MAX_PLAYERS);
  readonly tackleCooldown = new Int32Array(MAX_PLAYERS);
  readonly duelState = new Int32Array(MAX_PLAYERS);
  readonly duelTicks = new Int32Array(MAX_PLAYERS);
  readonly sprintLatch = new Uint8Array(MAX_PLAYERS);
  readonly sprintHold = new Int32Array(MAX_PLAYERS);
  readonly prevExposure = new Float64Array(MAX_PLAYERS);
  readonly inDuel = new Uint8Array(MAX_PLAYERS);
  readonly engPrevTick = new Int32Array(MAX_PLAYERS);
  readonly engPrevMargin = new Float64Array(MAX_PLAYERS);
  readonly engPrevBallDist = new Float64Array(MAX_PLAYERS);
  readonly engGraceTicks = new Int32Array(MAX_PLAYERS);
  readonly engStartTick = new Int32Array(MAX_PLAYERS).fill(-1);
  readonly engStartMargin = new Float64Array(MAX_PLAYERS);
  readonly engBestMargin = new Float64Array(MAX_PLAYERS);
  readonly engStartDist = new Float64Array(MAX_PLAYERS);

  /** §3 — the pass this team currently has in flight. See AiBrain's doc block. */
  passIntentTarget = -1;
  passIntentFrom = -1;
  passIntentTick = -1_000_000;

  /** Reaction delay clock, ticks. Non-zero ⇒ still acting on the old decision. */
  private readonly reactionTicks = new Int32Array(MAX_PLAYERS);

  /** Formation spread bookkeeping — how many of us share a Position. */
  private readonly posIndex = new Int32Array(MAX_PLAYERS);
  private readonly posCount = new Int32Array(MAX_PLAYERS);

  /** One pooled InputCommand per slot. The 60Hz loop allocates nothing (§15.7). */
  private readonly pool: InputCommand[] = new Array(MAX_PLAYERS);

  private readonly rngScratch: Pcg32 = pcg32Create(0, 0);
  /** Situational-shape scratch — pooled, so the 5Hz solve allocates nothing. */
  private readonly shapeSituation: ShapeSituation = createShapeSituation();
  private readonly shapeAdjust: ShapeAdjust = createShapeAdjust();
  private readonly steerReq: SteerRequest = createSteerRequest();
  private readonly moveOut: MoveOut = createMoveOut();
  private readonly seedBase: number;

  /**
   * Stimulus edge detection for the reaction clock. Derived, AI-local state —
   * not sim state, and deliberately not in WorldState: a wrong value costs one
   * reaction window, and putting it in the hashed state would make the AI part
   * of the determinism contract for no benefit.
   */
  private prevLastTouchTick = -1;
  private prevOwner = -2;
  private prevBallTeam = -1;
  private possChangeTick = -1_000_000;

  private primed = false;

  constructor(world: WorldState, team: 0 | 1, difficulty: Difficulty) {
    this.world = world;
    this.team = team;
    this.params = DIFFICULTY_PARAMS[difficulty];
    // Each team gets its own stream so the two AIs on one pitch never correlate.
    this.seedBase = mix32(world.config.seed ^ mix32(team + 1));
    for (let i = 0; i < MAX_PLAYERS; i++) this.pool[i] = emptyInput();
    this.markTarget.fill(-1);

    // Seed the edge detectors from the world as it is NOW, so construction does
    // not itself look like a turnover and freeze the whole team for 350ms.
    this.prevLastTouchTick = world.ball.lastTouchTick;
    this.prevOwner = world.ball.possessedBy;
    this.prevBallTeam = this.effectiveBallTeam(world);
    this.possChangeTick = -AI_TRANSITION_TICKS;
  }

  /** (matchSeed, tick, playerId, salt) → a generator. Stateless; see the header. */
  rngFor(id: number, salt: number): Pcg32 {
    const k = mix32(this.seedBase ^ mix32(id * 131 + salt * 7919)) ^ this.world.tick;
    pcg32Seed(this.rngScratch, mix32(k), this.team + 1);
    return this.rngScratch;
  }

  // -- the tick -------------------------------------------------------------

  roleName(id: number): string {
    return ASSIGNMENT_LABELS[this.assignment[id]!] ?? 'Hold';
  }

  produceInputs(world: WorldState, out: Array<InputCommand | null>): void {
    this.world = world;
    this.attackSign = (this.team === 0) === world.teamAAttacksPositiveX ? 1 : -1;

    // Capture which slots are human BEFORE we fill any: a non-null input here is
    // a slot the server already wrote (a human, or another controller). The
    // co-op pass bonus (§13) reads this so a carrier can prefer feeding the
    // human teammate. Snapshot, because our own writes below make out[] non-null.
    for (let id = 0; id < MAX_PLAYERS; id++) this.humanMask[id] = out[id] != null ? 1 : 0;

    // Call-for-pass (§8): a HUMAN teammate pressing X sets InputFlags.CallForPass
    // in their pre-filled command. Read it here (before our own writes overwrite
    // out[]) for every slot on OUR team — the caller is a teammate the bot carrier
    // should feed, and the AI does NOT own that human slot, so this must NOT be
    // gated on ownsSlot. Hold the call active for a short window so the ~10Hz
    // carrier decision has cycles to respond within ~0.25–0.75s.
    const players = world.players;
    for (let id = 0; id < MAX_PLAYERS; id++) {
      if (players.team[id] !== this.team || players.active[id] !== 1) {
        this.callActive[id] = 0;
        continue;
      }
      const cmd = out[id];
      if (cmd != null && (cmd.flags & InputFlags.CallForPass) !== 0) {
        this.callExpiryTick[id] = world.tick + AI_CALL_FOR_PASS_DURATION_TICKS;
      }
      this.callActive[id] = this.callExpiryTick[id]! > world.tick ? 1 : 0;
    }
    // §8 — a HUMAN teammate's single-middle-click CIRCULATE (TacticalCommand.
    // CirculatePass in their pre-filled command). Held for a short window like the
    // call. While it is live a BOT carrier plays a safe bot-to-bot pass now.
    for (let id = 0; id < MAX_PLAYERS; id++) {
      if (players.team[id] !== this.team || players.active[id] !== 1) continue;
      const cmd = out[id];
      if (cmd != null && cmd.tactical === TacticalCommand.CirculatePass) {
        this.circulateExpiryTick = world.tick + AI_CIRCULATE_DURATION_TICKS;
      }
    }
    // Only meaningful while a BOT of ours carries (the human circulating his OWN
    // ball is handled client-side, §5). Interrupt that carrier so it plays now.
    const carrier = world.ball.possessedBy;
    const botCarries = carrier >= 0 && players.team[carrier] === this.team && this.humanMask[carrier] !== 1;
    this.circulateActive = this.circulateExpiryTick > world.tick && botCarries;

    // §6 — a live call must INTERRUPT the carrier's dribble/hold, not wait for the
    // next 10Hz decision tick or a reaction delay. If any teammate is calling, the
    // ball-carrier re-decides EVERY tick until it responds (the strong call bonus
    // in decideOnBall then wins), so a valid request is played within ~0.25–0.75s.
    // §8 — a live circulate interrupts the bot carrier the same way.
    let teammateCalling = false;
    for (let id = 0; id < MAX_PLAYERS; id++) if (this.callActive[id] === 1) { teammateCalling = true; break; }

    // Phase-offset the two teams' Layer 3 work so a 22-player server never pays
    // for both formation solves on the same tick.
    const t = world.tick + this.team * (TEAM_PERIOD >> 1);
    const layer3Due = !this.primed || t % TEAM_PERIOD === 0;
    const layer2Due = !this.primed || t % INDIVIDUAL_PERIOD === 0;

    // The offside line is refreshed EVERY tick, not at 5Hz with the rest of
    // Layer 3. It is a hard constraint (§10.1) and a 200ms-stale line is a
    // striker standing a metre offside — which costs a goal, not a yard.
    this.updateOnsideLimit(world);

    if (layer3Due) {
      this.influence.rebuild(world, this.team);
      this.phase = this.determinePhase(world);
      this.solveFormation(world);
      this.assign(world);
    }

    // §3 — THE RECEIVER OF AN INTENTIONAL PASS IS A RECEIVER. Every tick, not at
    // 5Hz: a ground pass in this sim only travels ~9 m (ball-travel.ts), so up to
    // 11 ticks of a short pass' entire flight would otherwise be spent with the
    // intended man still holding his shape. See applyPassIntent.
    this.applyPassIntent(world);

    this.updateReactions(world);

    for (let id = 0; id < MAX_PLAYERS; id++) {
      if (!this.ownsSlot(world, id)) continue;
      // Non-null ⇒ somebody already owns this slot. See the TeamAI contract.
      if (out[id] != null) continue;

      if (this.kickCooldown[id]! > 0) this.kickCooldown[id]!--;
      if (this.tackleCooldown[id]! > 0) this.tackleCooldown[id]!--;
      if (this.sprintHold[id]! > 0) this.sprintHold[id]!--;
      // A duel is over the tick the ball stops being an opponent's — the duel
      // node is not consulted after that, so the exit has to be made here
      // (Commit B0.5).
      leaveDuelIfPossessionChanged(this, id);
      // Only ACTIVE duel time accumulates — otherwise a player who left the
      // duel keeps ageing his state and satisfies every minimum-hold on return.
      if (this.inDuel[id] === 1) this.duelTicks[id]!++;

      // ── Reaction delay (§10.2) ──────────────────────────────────────────
      // While the clock runs, Layer 2 does NOT re-decide. The player keeps
      // executing the decision he made before the ball moved — which is what a
      // reaction delay physically is. An AI that re-planned instantly and then
      // waited to act would be a machine pretending; this one is genuinely late.
      // EXCEPTION (§6): the ball-carrier with a teammate calling re-decides now,
      // every tick — a shout is heard immediately; it must interrupt the dribble.
      if ((teammateCalling || this.circulateActive) && world.ball.possessedBy === id) {
        this.reactionTicks[id] = 0;
        runIndividualAi(this, id);
      } else if (this.reactionTicks[id]! > 0) {
        this.reactionTicks[id]!--;
        if (this.reactionTicks[id]! === 0) runIndividualAi(this, id);
      } else if (layer2Due) {
        runIndividualAi(this, id);
      }

      this.steerFor(world, id);
      // Commit-B diagnostic (inert unless DUEL_SAMPLES=1): the 60 Hz view of an
      // ENGAGE episode, sampled after the decision AND the steer of this tick.
      sampleEngageEpisode(this, id, Math.hypot(this.moveOut.x, this.moveOut.z));
      const cmd = this.pool[id]!;
      cmd.seq = world.tick;
      cmd.tick = world.tick;
      applyDecisionToInput(this, id, this.moveOut.x, this.moveOut.z, cmd);
      out[id] = cmd;
    }

    this.primed = true;
  }

  /** Slots this AI may fill: our team, alive, outfield. */
  private ownsSlot(world: WorldState, id: number): boolean {
    const p = world.players;
    if (p.active[id] !== 1 || p.sentOff[id] === 1) return false;
    if (p.team[id]! !== this.team) return false;
    // sim/goalkeeper.ts is a complete keeper (§4.5) and treats a non-null input
    // as "a human has taken over". Filling this slot would replace a good AI
    // with a worse one.
    //
    // ONE EXCEPTION (§5.1): the keeper DESIGNATED to take a staged restart — in
    // practice a goal kick, which rules/fsm.ts pickRestartTaker now always gives
    // him. goalkeeper.ts has no restart awareness whatsoever (its whole body is
    // gated on `phase === InPlay`, and it releases any hold the moment a restart
    // is awarded), so with nothing filling this slot a keeper-taken restart had
    // NOBODY walking him to the ball: it expired on the Ready-stage timeout with
    // the ball sitting dead on the spot. individual.ts's decideRestart is the code
    // that takes a restart, and this is what lets it run for him.
    //
    // Narrow on purpose: only while the ball is dead AND he is the taker. The tick
    // play restarts, this returns false again and goalkeeper.ts has its slot back.
    if (world.positions[id] === 'GK') {
      const m = world.match;
      return m.phase !== MatchPhase.InPlay && m.restartTaker === id;
    }
    return true;
  }

  // -- Layer 1 call site ----------------------------------------------------

  private steerFor(world: WorldState, id: number): void {
    const a = this.assignment[id]!;
    const urgent = a === Assignment.Chase || a === Assignment.Press;
    // Shape is a pull toward where you belong. It must not apply to the jobs
    // that ARE leaving your position: carrying, chasing, pressing.
    //
    // ...nor to OFFERING, and that one needs its reason stated, because it looks
    // like a discipline hole and is the opposite. An offer point is ALREADY
    // clamped to within AI_OFFER_MAX_ANCHOR_DRIFT of the anchor (individual.ts
    // offerPoint step 3), so the shape spring here would be the SAME constraint
    // applied twice — and the second application is the one that wins, because
    // it pulls every tick while the clamp only bounds the target. MEASURED
    // (probe, 3 seeds x 2 min): with the spring on, an offering player sat 8.96 m
    // from his own seek point and ended up no more visible to the carrier than
    // his anchor already was (31.8 % vs 32.9 % inside the scan cone) — the
    // rotation was computed, and then steered straight back out of.
    const shapeless = urgent || a === Assignment.Carry || a === Assignment.Offer;

    const req = this.steerReq;
    req.id = id;
    req.seekX = this.decSeekX[id]!;
    req.seekZ = this.decSeekZ[id]!;
    req.shapeX = this.shapeX[id]!;
    req.shapeZ = this.shapeZ[id]!;
    // Positioning discipline (§10.3): a cognitive knob. It buys shape, never speed.
    req.shapeWeight = shapeless ? 0 : AI_STEER_SHAPE_WEIGHT * this.params.discipline;
    req.arriveRadius = urgent ? AI_SEEK_ARRIVE_RADIUS_URGENT : AI_SEEK_ARRIVE_RADIUS;
    req.attackSign = this.attackSign;
    req.onsideLimitProj = this.onsideLimitProj;
    req.onsideActive = this.onsideActiveFor(world);

    steer(world, req, this.moveOut);
  }

  // -- Offside (§5.4) -------------------------------------------------------

  /**
   * The attack-axis projection a player of this team must not exceed.
   *
   * Mirrors rules/offside.ts exactly: you are in an offside position only if you
   * are past the halfway line AND past the ball AND past the second-last
   * opponent. So the safe limit is the greatest of those three, and staying at
   * or behind it fails at least one leg of the test. Level is onside (§5.4's
   * strict >), which is why this is a limit and not a limit-minus-something —
   * the safety margin lives in steering.ts, where it belongs.
   */
  private updateOnsideLimit(world: WorldState): void {
    if (!world.config.offsideEnabled) {
      this.onsideLimitProj = Number.POSITIVE_INFINITY;
      return;
    }
    const p = world.players;
    const dir = this.attackSign;
    const opp = 1 - this.team;

    let firstProj = Number.NEGATIVE_INFINITY;
    let secondProj = Number.NEGATIVE_INFINITY;
    let defenders = 0;
    for (let i = 0; i < MAX_PLAYERS; i++) {
      if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
      if (p.team[i]! !== opp) continue;
      defenders++;
      const proj = dir * p.px[i]!;
      if (proj >= firstProj) {
        secondProj = firstProj;
        firstProj = proj;
      } else if (proj > secondProj) {
        secondProj = proj;
      }
    }

    // Fewer than two opponents ⇒ no line can be drawn ⇒ nobody can be offside.
    if (defenders < 2) {
      this.onsideLimitProj = Number.POSITIVE_INFINITY;
      return;
    }

    const ballProj = dir * world.ball.px;
    let limit = secondProj;
    if (ballProj > limit) limit = ballProj;
    if (limit < 0) limit = 0; // your own half is always onside
    this.onsideLimitProj = limit;
  }

  /** Offside can only be given against us when the ball is ours to receive. */
  private onsideActiveFor(world: WorldState): boolean {
    if (!world.config.offsideEnabled) return false;
    if (world.match.phase !== MatchPhase.InPlay) return false;
    const owner = world.ball.possessedBy;
    if (owner >= 0 && world.players.team[owner]! !== this.team) return false;
    return true;
  }

  // -- Phase FSM (§10.1) ----------------------------------------------------

  /** Who the ball belongs to right now: the carrier, else whoever touched last. */
  private effectiveBallTeam(world: WorldState): number {
    const owner = world.ball.possessedBy;
    if (owner >= 0) return world.players.team[owner]!;
    return world.ball.lastTouchedTeam;
  }

  private determinePhase(world: WorldState): TeamPhase {
    const ballTeam = this.effectiveBallTeam(world);
    if (ballTeam !== this.prevBallTeam && ballTeam >= 0 && this.prevBallTeam >= 0) {
      this.possChangeTick = world.tick;
    }
    this.prevBallTeam = ballTeam;

    // The moment right after the ball changes hands — counter-press or break.
    // Both teams are in TRANSITION at once, which is correct: it is one moment,
    // and the individual layer already knows which side of it each player is on.
    if (world.tick - this.possChangeTick < AI_TRANSITION_TICKS) return TeamPhase.Transition;

    const owner = world.ball.possessedBy;
    const ballFx = (this.attackSign * world.ball.px) / (world.config.pitchLength * 0.5);

    if (owner >= 0 && world.players.team[owner]! === this.team) {
      if (ballFx > AI_FINAL_THIRD_FX) return TeamPhase.FinalThird;
      if (ballFx < AI_BUILD_UP_FX) return TeamPhase.BuildUp;
      return TeamPhase.Attack;
    }
    if (owner >= 0) {
      // They have it. Press high in their half; sit in a block in ours.
      return ballFx > 0 ? TeamPhase.Press : TeamPhase.DefendBlock;
    }
    // Loose. Shape up as whoever touched it last would want to.
    return ballTeam === this.team ? TeamPhase.Attack : TeamPhase.DefendBlock;
  }

  // -- Formation solver (§10.1) ---------------------------------------------

  /**
   * Each role's target position from ball position + phase.
   *
   * Four terms, in order: the anchor from formations.ts, a lateral spread so two
   * CBs are not one CB, the phase shift (the block's height), and the ball slide
   * (the block's drift). Then discipline decides how much of the result survives
   * contact with the temptation to just run at the ball.
   */
  private solveFormation(world: WorldState): void {
    const halfL = world.config.pitchLength * 0.5;
    const halfW = world.config.pitchWidth * 0.5;
    // THE SHAPE LEADS THE BALL (AI_SHAPE_BALL_LEAD_S — see its doc, including
    // why the speed used is CAPPED at AI_SHAPE_BALL_LEAD_MAX_SPEED). Only the
    // block-shift terms below get the led position; the discipline `chase`
    // lerp further down deliberately keeps reacting to the ball's ACTUAL
    // current spot.
    const bvx = world.ball.vx, bvz = world.ball.vz;
    const bSpeed = Math.hypot(bvx, bvz);
    const leadScale = bSpeed > AI_SHAPE_BALL_LEAD_MAX_SPEED ? AI_SHAPE_BALL_LEAD_MAX_SPEED / bSpeed : 1;
    const leadX = world.ball.px + bvx * leadScale * AI_SHAPE_BALL_LEAD_S;
    const leadZ = world.ball.pz + bvz * leadScale * AI_SHAPE_BALL_LEAD_S;
    const ballFx = clamp((this.attackSign * leadX) / halfL, -1, 1);
    const ballFz = clamp((this.attackSign * leadZ) / halfW, -1, 1);
    // Damp the low-discipline ball-chase so a weak team looks LOOSE, not swarming:
    // the drift toward the ball was one of the coherent ballward pulls that made
    // half the block move the same way at once (AI_INDISCIPLINE_CHASE_SCALE).
    const chase =
      (1 - this.params.discipline) * AI_INDISCIPLINE_BALL_CHASE * AI_INDISCIPLINE_CHASE_SCALE;
    // Phases in which the block is genuinely attacking — used to release the
    // fullbacks up the wing (they hold their post in build-up and when defending).
    const attackingPhase = this.phase === TeamPhase.Attack || this.phase === TeamPhase.FinalThird;

    // With the ball, the WHOLE block climbs out of our own third so the carrier
    // has a standing outlet ahead of him — the fix for teams that won the ball
    // deep and then recycled it forever because nobody was forward to receive.
    const inPossession =
      this.phase === TeamPhase.BuildUp ||
      this.phase === TeamPhase.Attack ||
      this.phase === TeamPhase.FinalThird;

    this.countSharedPositions(world);

    // ── SITUATIONAL SHAPE (ai/situational-shape.ts) ─────────────────────────
    // The five rules that make the shape a function of the STATE OF THE GAME
    // and not just of the phase: compress out of possession / spread in it,
    // low-mid-high block by the ball's THIRD, the counter-attack and
    // counter-press windows, and scoreline + time remaining. Described once per
    // solve; applied per band inside the loop below.
    const sit = this.shapeSituation;
    const adj = this.shapeAdjust;
    describeShapeSituation(
      world,
      this.team,
      this.attackSign,
      inPossession,
      this.phase === TeamPhase.Transition,
      sit,
    );

    for (let id = 0; id < MAX_PLAYERS; id++) {
      if (!this.ownsSlot(world, id)) continue;
      const pos = world.positions[id]!;
      const anchor = POSITION_ANCHORS[pos];

      let shift = PHASE_SHIFT[this.phase]!;
      if (shift > 0 && isForwardPosition(pos)) shift *= AI_FORWARD_PHASE_GAIN;
      if (shift < 0) {
        if (isDefenderPosition(pos)) shift *= AI_DEFENDER_PHASE_GAIN;
        else if (isForwardPosition(pos)) shift *= AI_FORWARD_DROP_DAMP;
      }
      if (inPossession) {
        // Defenders join the push, but damped — a back line is still a back line.
        shift += isDefenderPosition(pos)
          ? AI_POSSESSION_PUSH * AI_POSSESSION_PUSH_DEFENDER_DAMP
          : AI_POSSESSION_PUSH;
        // ...except the FULLBACKS, who overlap up the wing when we are actually
        // attacking — width and a crossing option high up the pitch (§4.7). The
        // centre-backs stay home; that is what keeps this from being a swarm
        // forward instead of a swarm at the ball.
        if (attackingPhase && isFullbackPosition(pos)) shift += AI_FULLBACK_ATTACK_PUSH;
      } else if (this.phase === TeamPhase.Press) {
        // Press high: squeeze the whole game into their half so turnovers happen
        // near their goal, not ours — more shots for both sides.
        shift += AI_PRESS_LINE_PUSH;
      }

      const count = this.posCount[id]!;
      const spread = count > 1 ? (this.posIndex[id]! - (count - 1) * 0.5) * AI_SHAPE_SPREAD_FZ : 0;

      // The situational adjustment for THIS band, on top of the phase shift.
      situationalShape(sit, shapeBandOf(pos), adj);
      shift += adj.dFx;

      // The block still slides toward the ball's side, but LESS (SCALE): the full
      // slide moved every anchor the same way when the ball moved, so ~8 shape-
      // holders translated ballward in unison — the "copying" the user saw.
      // adj.widthScale scales the ANCHOR's width only — the duplicate-position
      // `spread` is anti-stacking, not width, and a narrow block still must not
      // let two centre-backs stand on each other.
      const fx = clamp(anchor.fx + shift + ballFx * AI_BLOCK_BALL_SHIFT_X * AI_BLOCK_BALL_SHIFT_SCALE, -1, 1);
      const fz = clamp(
        anchor.fz * adj.widthScale + spread + ballFz * AI_BLOCK_BALL_SHIFT_Z * AI_BLOCK_BALL_SHIFT_SCALE,
        -1,
        1,
      );

      // fz is mirrored by attackSign along with fx, so a team's left stays its
      // own left after the half-time switch of ends (formations.ts).
      let x = fx * halfL * this.attackSign;
      let z = fz * halfW * this.attackSign;

      // Positioning discipline (§10.3) — the ONLY thing difficulty does to shape.
      // Low discipline drifts toward the ball: not slower, not blinder, just
      // worse at staying where he is supposed to be. That is what a weak player
      // looks like, and it is invisible as a handicap.
      x = lerp(x, world.ball.px, chase);
      z = lerp(z, world.ball.pz, chase);

      // Keep the shape on the pitch — an anchor outside the touchline would make
      // steering push players into the boundary forever.
      this.shapeX[id] = clamp(x, -halfL + 1, halfL - 1);
      this.shapeZ[id] = clamp(z, -halfW + 1, halfW - 1);
    }
  }

  /** Index and count of teammates sharing each Position — drives the spread. */
  private countSharedPositions(world: WorldState): void {
    for (let id = 0; id < MAX_PLAYERS; id++) {
      if (!this.ownsSlot(world, id)) continue;
      let index = 0;
      let count = 0;
      for (let j = 0; j < MAX_PLAYERS; j++) {
        if (!this.ownsSlot(world, j)) continue;
        if (world.positions[j] !== world.positions[id]) continue;
        if (j < id) index++;
        count++;
      }
      this.posIndex[id] = index;
      this.posCount[id] = count;
    }
  }

  // -- Assignments (§10.1) --------------------------------------------------

  /**
   * Who does what for the next ~200ms.
   *
   * This is the layer that decides whether the AI looks like a team or like a
   * under-8s swarm, and the whole trick is that it says NO to most players. One
   * chaser, `pressingCount` pressers, everyone else holds. §10.3 scales pressing
   * aggression here and nowhere else.
   */
  private assign(world: WorldState): void {
    const p = world.players;
    for (let id = 0; id < MAX_PLAYERS; id++) {
      if (!this.ownsSlot(world, id)) continue;
      // Snapshot this defender's man BEFORE clearing, so the re-pick below can
      // prefer to keep it (marking hysteresis).
      this.prevMarkTarget[id] = this.markTarget[id]!;
      this.wasOffering[id] = this.assignment[id] === Assignment.Offer ? 1 : 0;
      this.assignment[id] = Assignment.HoldShape;
      this.markTarget[id] = -1;
    }

    const owner = world.ball.possessedBy;

    if (owner >= 0) {
      // §3 — the ball is under control: end the loose-ball commitment so the NEXT
      // loose episode picks a receiver fresh (the hysteresis is within one episode,
      // not carried across a possession). Leaving it stale made an irrelevant old
      // chaser win the hysteresis and the ball sit uncollected.
      this.looseChaser = -1;
      if (p.team[owner]! === this.team) this.assignAttack(world, owner);
      else this.assignDefence(world, owner);
      return;
    }
    this.assignLoose(world);
  }

  private assignAttack(world: WorldState, carrier: number): void {
    if (this.ownsSlot(world, carrier)) this.assignment[carrier] = Assignment.Carry;

    // Forwards always look for space — that is what a forward is.
    for (let id = 0; id < MAX_PLAYERS; id++) {
      if (!this.ownsSlot(world, id) || id === carrier) continue;
      if (isForwardPosition(world.positions[id]!)) this.assignment[id] = Assignment.Support;
    }

    // Plus the two nearest bodies, to give the carrier an angle he can actually
    // use. Nearest, not best-positioned: a support option 40m away is a pass he
    // cannot make (§4.2's speed band tops out at 25 m/s).
    const p = world.players;
    for (let n = 0; n < 2; n++) {
      let best = -1;
      let bestDist = Number.POSITIVE_INFINITY;
      for (let id = 0; id < MAX_PLAYERS; id++) {
        if (!this.ownsSlot(world, id) || id === carrier) continue;
        if (this.assignment[id] === Assignment.Support) continue;
        const d = distXZ(p.px[carrier]!, p.pz[carrier]!, p.px[id]!, p.pz[id]!);
        if (d < bestDist) {
          bestDist = d;
          best = id;
        }
      }
      if (best < 0) break;
      this.assignment[best] = Assignment.Support;
    }

    // ── THE OTHER FIVE — SHOW FOR THE BALL (owner, 2026-07-25: "each and every
    // player try their best", "make the game mostly pass based") ──────────────
    //
    // Everything above names FIVE players: a carrier, the forwards, and the two
    // nearest bodies. In a 4-4-2 that leaves the back four and a midfielder on
    // HoldShape, whose entire Layer-2 behaviour is "seek your formation anchor,
    // then nothing" — and a player standing on his anchor is, from the stands,
    // indistinguishable from a player who has crashed.
    //
    // So every remaining outfielder inside the band where a pass is actually
    // playable takes the OFFER job. individual.ts's offerPoint rotates him onto
    // the near edge of the carrier's visible sector AT THE RADIUS HE ALREADY
    // HAD — he shows for the ball, he does not run at it. There is deliberately
    // no cap: a cap would be the wrong tool, because the thing a cap protects
    // against (converging on the ball) is prevented by construction in the
    // geometry, not by counting bodies.
    //
    // MEASURED, 8 seeds x 5 sim-min, control re-run bit-identical to baseline:
    // purposeful outfielders 6.96/7.16 -> 7.75/7.77 (p10 2 -> 5), the worst two
    // roles RM 53.2 -> 62.3 % and CM 58.2 -> 64.7 %, passes 271/476 -> 305/512,
    // retention within a spell 56.3 -> 59.2 %. It did NOT do what it was aimed
    // at — the carrier's cone-rejection rate barely moved — and the AI_OFFER_*
    // block in constants.ts carries that trace in full. Read it before extending
    // this: the visibility argument is disproved, the retention one is not.
    //
    // Outside the band he keeps HoldShape and that is correct football: under
    // AI_OFFER_MIN_DISTANCE the lay-off already waives the cone for him, and
    // over AI_OFFER_MAX_DISTANCE he is not a pass — he is the rest defence.
    //
    // The band is STICKY (AI_OFFER_BAND_HYSTERESIS). Offering and holding shape
    // seek very different points, so a man sitting on a band edge who flips
    // between them every 200 ms is handed two targets in opposite directions —
    // which is the owner's "glitching" exactly, and it is what the first cut of
    // this change did: measured steering reversals went 0.261 -> 0.337 per
    // player-second. Widening the band by 3 m for a man who was already offering
    // costs nothing and removes the oscillation by construction.
    for (let id = 0; id < MAX_PLAYERS; id++) {
      if (!this.ownsSlot(world, id) || id === carrier) continue;
      if (this.assignment[id] !== Assignment.HoldShape) continue;
      const slack = this.wasOffering[id] === 1 ? AI_OFFER_BAND_HYSTERESIS : 0;
      const d = distXZ(p.px[carrier]!, p.pz[carrier]!, p.px[id]!, p.pz[id]!);
      if (d < AI_OFFER_MIN_DISTANCE - slack || d > AI_OFFER_MAX_DISTANCE + slack) continue;
      this.assignment[id] = Assignment.Offer;
    }
  }

  private assignDefence(world: WorldState, carrier: number): void {
    const p = world.players;
    const cx = p.px[carrier]!;
    const cz = p.pz[carrier]!;

    // A keeper HOLDING the ball cannot be pressed — the hold is protected and
    // the 6-second rule forces distribution. Sending a presser at gloves parks
    // an exhausted striker 9m off the keeper doing nothing (measured: the
    // majority of team-0 presser-drift misses were exactly this standoff).
    // Everyone marks/holds shape instead and recovers; the press resumes the
    // moment the ball is back at somebody's feet.
    const glovesHeld = p.gkHoldStartTick[carrier]! >= 0;

    // Pressers — THE ANTI-SWARM CAP (§10.1 Layer 3, §10.3). At most
    // AI_MAX_CONCURRENT_PRESSERS (=2) players ever run at the carrier: a primary
    // presser, plus at most one second presser who must already be CLOSE (a
    // support press, not a second man sprinting across the pitch to pile in). The
    // team AI designates them here; nobody decides for himself "I can see the ball,
    // I'll run at it" — that independent decision, taken by up to `pressingCount`
    // (3) defenders at once, WAS the swarm the user saw as "copying".
    //
    // §10.3 scales pressing aggression through the tier's pressingCount, but only
    // as EAGERNESS: it widens the second presser's join radius, it does not lift
    // the cap. Easy (pressingCount 1) never sends a second. Nobody sends a third.
    const secondRadius = Math.min(
      this.params.pressRadius,
      AI_SECOND_PRESS_RADIUS + (this.params.pressingCount - 1) * AI_SECOND_PRESS_RADIUS_PER_COUNT,
    );
    const maxPressers = glovesHeld
      ? 0
      : Math.min(this.params.pressingCount, AI_MAX_CONCURRENT_PRESSERS);
    let assigned = 0;
    for (let n = 0; n < maxPressers; n++) {
      // The first presser may engage from the full pressRadius; every one after
      // him must be within the (tighter) second-press radius of the carrier.
      const radius = n === 0 ? this.params.pressRadius : secondRadius;
      const best = this.nearestPresserCandidate(world, cx, cz, radius);
      if (best < 0) break;
      this.assignment[best] = Assignment.Press;
      assigned++;
    }
    // Nobody in range ⇒ the single nearest still goes, or the AI would stand off a
    // carrier forever. Still one body, not a swarm. (Not against held gloves.)
    if (assigned === 0 && !glovesHeld) {
      const nearest = this.nearestUnassigned(world, cx, cz);
      if (nearest >= 0) this.assignment[nearest] = Assignment.Press;
    }

    // Markers: each defender picks up the nearest unmarked opponent. Greedy in
    // id order — not optimal, but a Hungarian assignment at 5Hz would buy
    // nothing a viewer could see.
    for (let id = 0; id < MAX_PLAYERS; id++) {
      if (!this.ownsSlot(world, id)) continue;
      if (this.assignment[id] !== Assignment.HoldShape) continue;
      if (!isDefenderPosition(world.positions[id]!)) continue;
      const t = this.nearestUnmarkedOpponent(world, id, carrier);
      if (t < 0) continue;
      this.assignment[id] = Assignment.Mark;
      this.markTarget[id] = t;
    }

    // One midfielder screens the space in front of the back line.
    for (let id = 0; id < MAX_PLAYERS; id++) {
      if (!this.ownsSlot(world, id)) continue;
      if (this.assignment[id] !== Assignment.HoldShape) continue;
      const pos = world.positions[id]!;
      if (isDefenderPosition(pos) || isForwardPosition(pos)) continue;
      this.assignment[id] = Assignment.Cover;
      break;
    }
  }

  /**
   * §3 — ticks for player `id` to reach a MOVING ball: a fixed-point on "where
   * the ball will be when I get there", so the receiver meets a pass downfield
   * rather than chasing where it WAS. Smaller = a better receiver.
   *
   * Solved with the SAME model individual.ts's interceptPoint runs
   * (ai/ball-travel.ts), and that is the point of the change: this function
   * picks the chaser and interceptPoint tells him where to go, so the two must
   * agree about the ball. They used to share a model that was wrong by +75 % on
   * a 6 m/s ball, which meant the wrong man was chosen AND sent to the wrong
   * place. See interceptPoint's header for the measured table.
   */
  private interceptTicks(world: WorldState, id: number): number {
    const b = world.ball;
    const p = world.players;
    solveIntercept(
      p.px[id]!, p.pz[id]!,
      b.px, b.py, b.pz,
      b.vx, b.vy, b.vz,
      MAX_SPEED_RUN, AI_INTERCEPT_ITERATIONS, interceptScratch,
    );
    return interceptScratch.t * SIMULATION_TICK_RATE;
  }

  private assignLoose(world: WorldState): void {
    // §3 — the receiver is the teammate with the SOONEST reachable intercept (where
    // the ball is heading), not the one nearest to where it is now: a firm pass is
    // met downfield, not chased from behind — the "receiving bots don't move to the
    // ball" fix. Committed via hysteresis so the primary chaser doesn't flip every
    // 5Hz decision as the ball rolls (which read as nobody being interested).
    let best = -1;
    let bestT = Number.POSITIVE_INFINITY;
    for (let id = 0; id < MAX_PLAYERS; id++) {
      if (!this.ownsSlot(world, id)) continue;
      const t = this.interceptTicks(world, id);
      if (t < bestT) { bestT = t; best = id; }
    }
    // Hysteresis: keep the committed chaser unless a rival beats him by a margin.
    const prev = this.looseChaser;
    if (prev >= 0 && prev !== best && this.ownsSlot(world, prev)) {
      const prevT = this.interceptTicks(world, prev);
      if (prevT <= bestT + AI_RECEIVE_HYSTERESIS_TICKS) best = prev;
    }
    this.looseChaser = best;
    if (best >= 0) this.assignment[best] = Assignment.Chase;

    // §7 — ONE support runner backs the chaser up (an outlet if he loses the 50/50)
    // so the team is organised around the loose ball WITHOUT a swarm: the
    // next-best-placed teammate supports; everyone else holds the shape, which the
    // formation solver has already shifted toward the ball side (tactical closing).
    if (best >= 0) {
      let support = -1;
      let supportT = Number.POSITIVE_INFINITY;
      for (let id = 0; id < MAX_PLAYERS; id++) {
        if (!this.ownsSlot(world, id) || id === best) continue;
        const t = this.interceptTicks(world, id);
        if (t < supportT) { supportT = t; support = id; }
      }
      if (support >= 0) this.assignment[support] = Assignment.Support;
    }

    // ── THE OTHER EIGHT (owner, 2026-07-25: "each and every player try their
    // best") ──────────────────────────────────────────────────────────────────
    //
    // Everything above assigns TWO players. Every remaining outfielder was left
    // on HoldShape, whose entire Layer-2 behaviour is "seek your formation
    // anchor" — and once he is standing on it, nothing. MEASURED
    // (ai/ball-engagement.test.ts): while the ball was loose the assignment mix
    // was ~80 % Hold, and a Hold player parked on his anchor is, from the stands,
    // indistinguishable from a player who has crashed. That is the other half of
    // "the bots are glitching / ignoring the ball", and it is why one man moving
    // while nine stand still reads as "the bot is HARDCODED to the ball".
    //
    // The fix is emphatically NOT more players going to the ball — that is the
    // swarm ai/loose-ball-organisation.test.ts exists to prevent, and it would
    // destroy the pass-based game. It is that a loose ball is a DEFENSIVE
    // SITUATION for everyone who is not contesting it, and this team AI already
    // knows how to organise one: assignDefence's marking and screening pass. It
    // was simply never run when nobody had the ball, as though a ball rolling
    // loose meant there was nothing to defend.
    //
    // So: pick up a man, or screen the space in front of the back line. Nobody
    // moves toward the ball who was not already going there, the Chase cap is
    // untouched, and every player now has a job that is about the actual game
    // state rather than about a spot on a diagram.
    this.assignLooseMarking(world);
  }

  /**
   * Marking + cover for everyone not contesting a loose ball. Deliberately the
   * SAME two rules assignDefence uses (nearest unmarked opponent, then one
   * midfield screen), so a loose ball and an opponent in possession organise the
   * team the same way and nothing new has to be tuned. `carrier` is -1 because
   * there is no carrier — nearestUnmarkedOpponent takes that as "no exclusion".
   */
  private assignLooseMarking(world: WorldState): void {
    for (let id = 0; id < MAX_PLAYERS; id++) {
      if (!this.ownsSlot(world, id)) continue;
      if (this.assignment[id] !== Assignment.HoldShape) continue;
      if (!isDefenderPosition(world.positions[id]!)) continue;
      const t = this.nearestUnmarkedOpponent(world, id, -1);
      if (t < 0) continue;
      this.assignment[id] = Assignment.Mark;
      this.markTarget[id] = t;
    }
    for (let id = 0; id < MAX_PLAYERS; id++) {
      if (!this.ownsSlot(world, id)) continue;
      if (this.assignment[id] !== Assignment.HoldShape) continue;
      const pos = world.positions[id]!;
      if (isDefenderPosition(pos) || isForwardPosition(pos)) continue;
      this.assignment[id] = Assignment.Cover;
      break;
    }
  }

  /**
   * §3 — MAKE THE INTENDED RECEIVER COME TO MEET THE BALL.
   *
   * ── The decision that was wrong ─────────────────────────────────────────────
   *
   * `assignLoose` picks ONE chaser: the teammate with the soonest reachable
   * intercept. That is exactly right for a genuine loose ball — a ricochet, a
   * clearance, a tackle spilling out — and exactly wrong for a PASS, because a
   * pass is not a loose ball. `decideOnBall` chose that man for a reason: he was
   * the one in space, with a clear lane, facing the right way. Throwing the
   * identity away at the moment of the strike and re-deriving "whoever is
   * closest to the ball's path" hands the ball to a body who does not know it is
   * coming, and leaves the man it was played to standing in his shape slot.
   *
   * MEASURED (chance-creation.test.ts, 4 seeds x 5 sim-min, before this change):
   * the man the ball was aimed at was the team's assigned receiver only 66 % of
   * the time; completed passes per spell of possession were 0.94 with a MEDIAN
   * OF ZERO — i.e. the median spell of possession contained no completed pass at
   * all. A pass-based game cannot be built on a one-in-three chance that the
   * intended man even sets off.
   *
   * ── What this does, and what it deliberately does NOT do ────────────────────
   *
   * While a pass of ours is in flight, the INTENDED receiver takes the Chase
   * assignment and the man `assignLoose` would have chosen drops to Support (he
   * becomes the second-ball man, which is what a supporting player is for). The
   * cap is unchanged: still ONE chaser plus ONE support, everyone else holds the
   * shape. This is not "more players go to the ball" — it is *the right one*.
   *
   * A pass stops being in flight the instant anybody ELSE touches the ball
   * (`ball.lastTouchedBy` is no longer the striker), the instant somebody gains
   * possession, or after AI_PASS_INTENT_TICKS — whichever comes first. No pass in
   * this sim lives longer than that, and an expired intent must not keep a man
   * running at a ball that is no longer his. (Testing `lastTouchTick` instead of
   * `lastTouchedBy` is the trap — see the AiBrain doc block.)
   *
   * The intent LOSES to a teammate who is clearly better placed
   * (AI_PASS_INTENT_MARGIN_TICKS): a badly over-hit ball is somebody else's now,
   * and "leave it" is football. It is a strong bias toward the intent, not an
   * override — which is also what stops a hopeless 40 m chase.
   *
   * Read-only over `world`, no RNG, and no new allocation: it only re-labels
   * entries in `assignment`, which Layer 2 reads next.
   */
  private applyPassIntent(world: WorldState): void {
    const target = this.passIntentTarget;
    if (target < 0) return;

    // Expired, already collected, or overtaken by any touch at all ⇒ not a pass
    // any more. Clearing the record here (rather than letting it rot) is what
    // keeps a second pass in the same possession from inheriting the first's man.
    const b = world.ball;
    if (
      world.tick - this.passIntentTick > AI_PASS_INTENT_TICKS ||
      b.lastTouchedBy !== this.passIntentFrom ||
      b.possessedBy >= 0 ||
      world.match.phase !== MatchPhase.InPlay ||
      !this.ownsSlot(world, target)
    ) {
      this.passIntentTarget = -1;
      return;
    }

    // Somebody CLEARLY better placed keeps it — see the header. Comparing with
    // the same ball model interceptTicks uses everywhere else (ball-travel.ts).
    const tIntended = this.interceptTicks(world, target);
    let bestT = Number.POSITIVE_INFINITY;
    for (let id = 0; id < MAX_PLAYERS; id++) {
      if (!this.ownsSlot(world, id) || id === target) continue;
      const t = this.interceptTicks(world, id);
      if (t < bestT) bestT = t;
    }
    if (bestT + AI_PASS_INTENT_MARGIN_TICKS < tIntended) return;

    // Hand the chase to the man it was played to; the previous chaser supports.
    const prev = this.looseChaser;
    if (prev >= 0 && prev !== target && this.ownsSlot(world, prev)) {
      if (this.assignment[prev] === Assignment.Chase) this.assignment[prev] = Assignment.Support;
    }
    this.looseChaser = target;
    this.assignment[target] = Assignment.Chase;
  }

  /** §3 — the committed receiver/chaser this team has, or -1. Read by the reaction
   *  gate so he reacts to a pass at once, and exposed for diagnostics. */
  receiverId(): number {
    return this.looseChaser;
  }

  /**
   * The best still-holding-shape player to send pressing the carrier at (cx,cz):
   * nearest within `radius`, penalized for not being able to see him (§10.2).
   * Only HoldShape players are eligible, so an already-assigned presser is never
   * re-picked — which is what keeps the presser loop from stacking the same body.
   * -1 if nobody unassigned is in range.
   */
  private nearestPresserCandidate(world: WorldState, cx: number, cz: number, radius: number): number {
    const p = world.players;
    let best = -1;
    let bestScore = Number.POSITIVE_INFINITY;
    for (let id = 0; id < MAX_PLAYERS; id++) {
      if (!this.ownsSlot(world, id)) continue;
      if (this.assignment[id] !== Assignment.HoldShape) continue;
      const d = distXZ(p.px[id]!, p.pz[id]!, cx, cz);
      if (d > radius) continue;
      const score = d + (canSee(world, id, cx, cz) ? 0 : AI_BLIND_DISTANCE_PENALTY);
      if (score < bestScore) {
        bestScore = score;
        best = id;
      }
    }
    return best;
  }

  /** Nearest of ours to (x,z), penalized for not seeing it. -1 if none. */
  private nearestUnassigned(world: WorldState, x: number, z: number): number {
    const p = world.players;
    let best = -1;
    let bestScore = Number.POSITIVE_INFINITY;
    for (let id = 0; id < MAX_PLAYERS; id++) {
      if (!this.ownsSlot(world, id)) continue;
      if (this.assignment[id] !== Assignment.HoldShape) continue;
      const d = distXZ(p.px[id]!, p.pz[id]!, x, z);
      const score = d + (canSee(world, id, x, z) ? 0 : AI_BLIND_DISTANCE_PENALTY);
      if (score < bestScore) {
        bestScore = score;
        best = id;
      }
    }
    return best;
  }

  private nearestUnmarkedOpponent(world: WorldState, id: number, carrier: number): number {
    const p = world.players;
    const opp = 1 - this.team;
    let best = -1;
    let bestDist = Number.POSITIVE_INFINITY;
    for (let o = 0; o < MAX_PLAYERS; o++) {
      if (p.active[o] !== 1 || p.sentOff[o] === 1) continue;
      if (p.team[o]! !== opp) continue;
      if (o === carrier) continue; // he is the pressers' problem
      if (world.positions[o] === 'GK') continue;
      if (this.isMarkedAlready(world, o)) continue;
      const d = distXZ(p.px[id]!, p.pz[id]!, p.px[o]!, p.pz[o]!);
      if (d < bestDist) {
        bestDist = d;
        best = o;
      }
    }
    // Marking HYSTERESIS: if the man I marked last cycle is still a legal,
    // unmarked target, keep him unless the new nearest is CLEARLY closer
    // (beyond AI_MARK_SWAP_MARGIN). Stops two defenders trading two attackers
    // back and forth every 5Hz — the classic robotic-marking shimmer.
    const prev = this.prevMarkTarget[id]!;
    if (
      prev >= 0 && prev !== best &&
      p.active[prev] === 1 && p.sentOff[prev] !== 1 &&
      p.team[prev] === opp && prev !== carrier &&
      world.positions[prev] !== 'GK' && !this.isMarkedAlready(world, prev)
    ) {
      const dPrev = distXZ(p.px[id]!, p.pz[id]!, p.px[prev]!, p.pz[prev]!);
      if (dPrev <= bestDist + AI_MARK_SWAP_MARGIN) return prev;
    }
    return best;
  }

  private isMarkedAlready(world: WorldState, opponent: number): boolean {
    for (let id = 0; id < MAX_PLAYERS; id++) {
      if (!this.ownsSlot(world, id)) continue;
      if (this.markTarget[id] === opponent) return true;
    }
    return false;
  }

  // -- Reaction delay (§10.2) -----------------------------------------------

  /**
   * Start every AI player's reaction clock when the situation changes.
   *
   * The stimulus is the ball being struck or changing owner — the two events a
   * footballer actually reacts TO. Anything less than 150ms would be a machine;
   * the band is enforced by reactionMsToTicks() and cannot be escaped from here.
   *
   * ⚠ A REACTION, ONCE STARTED, RUNS TO COMPLETION — a clock that is already
   * counting down is NOT re-armed. This is not a nicety; it is load-bearing.
   * The naive version re-armed every player on every ball-touch, and in a
   * scramble the ball is touched faster than a fast tier's ~10-tick clock, so
   * the clock never reached zero, the behaviour tree never re-ran, and the whole
   * team froze on stale decisions — a runaway loop (frozen players → ball bumps
   * around untrapped → more touches → still frozen). Measured on `hard`: the
   * ball stayed loose 100% of a 3-minute match and neither team completed a
   * single pass. Letting the clock finish breaks the loop: a player arms once,
   * waits his reaction, acts, then decides at 10Hz until the NEXT settled
   * stimulus — which is what a reaction delay actually is.
   *
   * ⚠ Known interaction, stated honestly: reactionMsToTicks clamps the total to
   * [AI_REACTION_MIN_MS, AI_REACTION_MAX_MS] = 150..350. On `easy` the base is
   * already 330, so the 120ms vision penalty is almost entirely swallowed by the
   * 350 ceiling and a blind easy AI reacts barely slower than a sighted one. The
   * band is the frozen contract (difficulty.ts) and the ceiling is the thing
   * protecting the player from a comically slow AI, so this is the right trade —
   * but the vision penalty only really bites on `normal` and `hard`.
   */
  private updateReactions(world: WorldState): void {
    const b = world.ball;
    const struck = b.lastTouchTick !== this.prevLastTouchTick;
    const ownerChanged = b.possessedBy !== this.prevOwner;
    this.prevLastTouchTick = b.lastTouchTick;
    this.prevOwner = b.possessedBy;
    if (!struck && !ownerChanged) return;

    for (let id = 0; id < MAX_PLAYERS; id++) {
      if (!this.ownsSlot(world, id)) continue;
      // Do not restart a reaction already in progress — see the header warning.
      if (this.reactionTicks[id]! > 0) continue;
      // YOU DO NOT REACT TO YOUR OWN TOUCH. The stimulus loop above fires on
      // every change of b.lastTouchTick, and a dribbling carrier knocks the ball
      // ~9 times a second (sim/ball.ts) — so the man ON the ball was arming a
      // 150-350ms reaction delay against his own foot, over and over, and could
      // not re-decide inside it. That is a machine being surprised by itself, and
      // it is dwell on exactly the player the owner is watching. MEASURED: 5.2 %
      // of all arms were self-inflicted, and every one of them landed on a
      // carrier. Everyone ELSE still reacts to his touch, which is the point of
      // the model (§10.2).
      if (b.lastTouchedBy === id) continue;
      const sees = canSee(world, id, b.px, b.pz);
      // Uniform jitter: two AI players never react on the same tick, which is
      // most of what stops a defensive line from moving like a rig.
      const jitter = (pcg32Float(this.rngFor(id, SALT_REACTION)) * 2 - 1) * this.params.reactionJitterMs;
      const ms = this.params.reactionMs + jitter + (sees ? 0 : this.params.visionPenaltyMs);
      this.reactionTicks[id] = reactionMsToTicks(ms, SIMULATION_TICK_RATE);
    }
  }
}

/**
 * Build a TeamAI for `team`.
 *
 * `world` is captured only to seed the RNG and the stimulus edge detectors; the
 * instance re-reads the world from produceInputs' parameter on every tick, so
 * passing a cloned or restored world later is fine.
 */
export function createTeamAI(world: WorldState, team: 0 | 1, difficulty: Difficulty): TeamAI {
  return new TeamAiImpl(world, team, difficulty);
}
